import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatIconModule} from '@angular/material/icon';
import {ActivatedRoute} from '@angular/router';
import {Store} from '@ngrx/store';
import {map} from 'rxjs';
import {ChallengeAlert, ChallengeView, ChallengeViewCycle} from 'src/app/core/model/project';
import {User} from 'src/app/core/model/user';
import {selectActiveUser} from 'src/app/core/store/active-user/active-user.selector';
import {UpdateAlertAction} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {
  GetProjectChallengeAction,
  UpdateChallengeCycleSuccessThresholdAction,
} from 'src/app/core/store/projects/projects.action';
import {selectProjectChallengeMap} from 'src/app/core/store/projects/projects.selector';
import {OphMetricBarChartComponent} from 'src/app/shared/design/oph-metric-bar-chart/oph-metric-bar-chart.component';
import {CbScheduleChipComponent} from 'src/app/projects/challenge-builder/shared/cb-schedule-chip.component';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {OphSlideToggleComponent} from 'src/app/shared/design/oph-slide-toggle/oph-slide-toggle.component';
import {CbTargetDisplayPipe} from 'src/app/shared/pipes/challenge-target-display.pipe';
import {CycleDisplayItemsPipe} from 'src/app/shared/pipes/cycle-display-items.pipe';
import {FormatAlertTimePipe} from 'src/app/shared/pipes/format-alert-time.pipe';
import {IsAfterActivePipe} from 'src/app/shared/pipes/is-after-active.pipe';
import {MeterDashOffsetPipe} from 'src/app/shared/pipes/meter-dash-offset.pipe';
import {NotificationDaysPipe} from 'src/app/shared/pipes/notification-days.pipe';
import {NumberAbbreviationPipe} from 'src/app/shared/pipes/number-abbreviation.pipe';
import {ScheduleChipTypePipe} from 'src/app/shared/pipes/schedule-chip-type.pipe';
import {ThresholdLabelPipe} from 'src/app/shared/pipes/threshold-label.pipe';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';

export type OutlineTab = 'tasks' | 'metrics' | 'targets';
export type MoreTab = 'milestones' | 'notifications';

@Component({
  selector: 'projects-challenge',
  standalone: true,
  imports: [
    OphLoadingModule,
    MatIconModule,
    NumberAbbreviationPipe,
    CbScheduleChipComponent,
    OphSlideToggleComponent,
    OphMetricBarChartComponent,
    IsAfterActivePipe,
    ScheduleChipTypePipe,
    CbTargetDisplayPipe,
    FormatAlertTimePipe,
    NotificationDaysPipe,
    ThresholdLabelPipe,
    MeterDashOffsetPipe,
    CycleDisplayItemsPipe,
  ],
  templateUrl: './projects-challenge.component.html',
  styleUrl: './projects-challenge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsChallengeComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly hostEl = inject(ElementRef).nativeElement as HTMLElement;

  readonly TrackerMetricTypes = TrackerMetricTypes;

  @ViewChild('outlineScroll') private outlineScrollEl?: ElementRef<HTMLElement>;
  @ViewChild('moreScroll') private moreScrollEl?: ElementRef<HTMLElement>;

  readonly outlineHasScroll = signal(false);
  readonly moreHasScroll = signal(false);

  readonly projectId = toSignal(this.route.paramMap.pipe(map(p => p.get('projectId') ?? null)), {
    initialValue: null as string | null,
  });

  private readonly activeUser = toSignal(this.store.select(selectActiveUser), {
    initialValue: null as User | null,
  });

  private readonly challengeMap = toSignal(this.store.select(selectProjectChallengeMap), {
    initialValue: {} as Record<string, ChallengeView>,
  });

  readonly challengeView = computed(() => {
    const id = this.projectId();
    const challengeMap = this.challengeMap();
    return id && challengeMap?.[id] ? challengeMap[id] : null;
  });

  readonly loading = signal(true);

  readonly outlineTab = signal<OutlineTab>('tasks');
  readonly moreTab = signal<MoreTab>('milestones');

  readonly outlineDirection = signal<'left' | 'right' | null>(null);
  readonly moreDirection = signal<'left' | 'right' | null>(null);

  readonly coinCount = computed(() => {
    const user = this.activeUser();
    if (!user?.coins?.length) return 0;
    return user.coins.reduce((sum, coin) => sum + (coin.coinCount || 0), 0);
  });

  readonly cycleLength = computed(() => {
    const cv = this.challengeView();
    return cv?.currentCycle?.length ?? 7;
  });

  constructor() {
    effect(
      () => {
        const id = this.projectId();
        if (!id) return;
        this.loading.set(true);
        this.store.dispatch(
          new GetProjectChallengeAction({
            projectId: id,
            onSuccess: () => this.loading.set(false),
            onFailure: () => this.loading.set(false),
          })
        );
      },
      {allowSignalWrites: true}
    );

    // Re-check scroll presence when content changes
    effect(
      () => {
        this.loading();
        this.outlineTab();
        this.moreTab();
        this.challengeView();
        setTimeout(() => this.checkScrollState(), 50);
      },
      {allowSignalWrites: true}
    );
  }

  private checkScrollState(): void {
    const outlineEl = this.outlineScrollEl?.nativeElement;
    const moreEl = this.moreScrollEl?.nativeElement;
    if (outlineEl) {
      this.outlineHasScroll.set(outlineEl.scrollHeight > outlineEl.clientHeight);
    }
    if (moreEl) {
      this.moreHasScroll.set(moreEl.scrollHeight > moreEl.clientHeight);
    }
    this.hostEl.querySelectorAll<HTMLElement>('.cycle-metrics-scroll').forEach(el => {
      this.updateMetricsFade(el);
    });
  }

  onMetricsScroll(event: Event): void {
    this.updateMetricsFade(event.target as HTMLElement);
  }

  private updateMetricsFade(el: HTMLElement): void {
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    el.parentElement?.classList.toggle('at-scroll-end', atEnd);
  }

  private static readonly OUTLINE_ORDER: OutlineTab[] = ['tasks', 'metrics', 'targets'];
  private static readonly MORE_ORDER: MoreTab[] = ['milestones', 'notifications'];

  selectOutlineTab(tab: OutlineTab): void {
    if (tab === this.outlineTab()) return;
    const current = ProjectsChallengeComponent.OUTLINE_ORDER.indexOf(this.outlineTab());
    const next = ProjectsChallengeComponent.OUTLINE_ORDER.indexOf(tab);
    const dir = next > current ? 'right' : 'left';
    this.outlineDirection.set(null);
    this.outlineTab.set(tab);
    requestAnimationFrame(() => this.outlineDirection.set(dir));
  }

  selectMoreTab(tab: MoreTab): void {
    if (tab === this.moreTab()) return;
    const current = ProjectsChallengeComponent.MORE_ORDER.indexOf(this.moreTab());
    const next = ProjectsChallengeComponent.MORE_ORDER.indexOf(tab);
    const dir = next > current ? 'right' : 'left';
    this.moreDirection.set(null);
    this.moreTab.set(tab);
    requestAnimationFrame(() => this.moreDirection.set(dir));
  }

  onToggleAlert(alert: ChallengeAlert, active: boolean): void {
    const projectId = this.projectId();
    if (!projectId || !alert._id) return;
    const updatedAlert = {...alert, active};

    this.store.dispatch(
      new UpdateAlertAction({
        projectId,
        alert: updatedAlert,
      })
    );
  }

  // ── Cycles Section ──

  readonly thresholdTabs = [
    {label: 'Easy', value: 25},
    {label: 'Medium', value: 50},
    {label: 'Hard', value: 75},
    {label: 'Perfect', value: 100},
  ];

  readonly circumference = 2 * Math.PI * 10;

  readonly sortedCycles = computed(() => {
    const cv = this.challengeView();
    if (!cv?.cycles?.length) return [];
    return [...cv.cycles].sort((a, b) => b.cycleNumber - a.cycleNumber);
  });

  onThresholdChange(cycle: ChallengeViewCycle, cycleSuccessThreshold: number): void {
    const projectId = this.projectId();
    if (!projectId) return;
    this.store.dispatch(
      new UpdateChallengeCycleSuccessThresholdAction({
        projectId,
        cycleNumber: cycle.cycleNumber,
        cycleSuccessThreshold,
      })
    );
  }
}
